import React, { useState, useRef, useEffect } from 'react';
import APP_COLOURS from '../../config/colors';
import TraqLogo from '../TraqLogo';
import ThemeToggle from '../ThemeToggle';

const Header = ({ userData, onLogout, onProfileClick }) => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isMobile = screenWidth < 768;

  const getInitials = () => {
    if (!userData) return 'M';
    const first = userData.first_name?.[0] || '';
    const last = userData.last_name?.[0] || '';
    return (first + last).toUpperCase() || 'M';
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b"
      style={{
        backgroundColor: `${APP_COLOURS.BG}ee`,
        borderColor: `${APP_COLOURS.BLUE}20`,
      }}
    >
      <div 
        className="max-w-[1920px] mx-auto flex items-center justify-between"
        style={{
          padding: isMobile ? '8px 10px' : '16px 24px',
        }}
      >
        {/* Logo */}
        <div className={`flex items-center ${isMobile ? 'gap-2' : 'gap-3'}`}>
          <TraqLogo height={isMobile ? 28 : 40} />
          <div>
            <p className={`opacity-70 ${isMobile ? 'text-[10px]' : 'text-xs'}`} style={{ color: APP_COLOURS.TEXTCOLOR }}>
              Manager Portal
            </p>
          </div>
        </div>

        <div className={`flex items-center ${isMobile ? 'gap-1.5' : 'gap-3'}`}>
          <ThemeToggle compact={isMobile} />

          {/* Manager Profile */}
          <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className={`flex items-center rounded-xl transition-all duration-200 ${isMobile ? 'gap-2 px-2 py-1.5' : 'gap-3 px-4 py-2 hover:scale-105'}`}
            style={{
              backgroundColor: `${APP_COLOURS.BLUE}20`,
              border: `1px solid ${APP_COLOURS.BLUE}40`,
            }}
          >
            {!isMobile && (
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  {userData?.first_name} {userData?.last_name}
                </p>
                <p className="text-xs opacity-70" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  Manager
                </p>
              </div>
            )}
            <div
              className={`rounded-full flex items-center justify-center text-white font-bold ${isMobile ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm'}`}
              style={{
                background: `linear-gradient(135deg, ${APP_COLOURS.BLUE}, ${APP_COLOURS.PURPLE})`,
              }}
            >
              {getInitials()}
            </div>
          </button>

          {/* Profile Dropdown Menu */}
          {showProfileMenu && (
            <div
              className={`absolute right-0 mt-2 rounded-xl shadow-2xl overflow-hidden animate-slide-in ${isMobile ? 'w-52' : 'w-64'}`}
              style={{
                backgroundColor: APP_COLOURS.CARD,
                border: `1px solid ${APP_COLOURS.BLUE}40`,
              }}
            >
              <div
                className={`border-b ${isMobile ? 'px-3 py-2' : 'px-4 py-3'}`}
                style={{ borderColor: `${APP_COLOURS.BLUE}20` }}
              >
                <p className={`font-semibold ${isMobile ? 'text-xs' : 'text-sm'}`} style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  {userData?.first_name} {userData?.last_name}
                </p>
                <p className={`opacity-70 mt-0.5 ${isMobile ? 'text-[10px]' : 'text-xs'}`} style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  {userData?.email}
                </p>
                {userData?.organisationID && (
                  <p className={`opacity-50 mt-0.5 ${isMobile ? 'text-[10px]' : 'text-xs'}`} style={{ color: APP_COLOURS.TEXTCOLOR }}>
                    Org: {userData.organisationID}
                  </p>
                )}
              </div>

              {userData?.brands && userData.brands.length > 0 && (
                <div
                  className={`border-b ${isMobile ? 'px-3 py-2' : 'px-4 py-3'}`}
                  style={{ borderColor: `${APP_COLOURS.BLUE}20` }}
                >
                  <p className={`opacity-70 ${isMobile ? 'text-[10px] mb-1' : 'text-xs mb-2'}`} style={{ color: APP_COLOURS.TEXTCOLOR }}>
                    Assigned Brands:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {userData.brands.map((brand, idx) => (
                      <span
                        key={idx}
                        className={`rounded-md ${isMobile ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs'}`}
                        style={{
                          backgroundColor: `${APP_COLOURS.BLUE}20`,
                          color: APP_COLOURS.BLUE,
                        }}
                      >
                        {brand}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={() => {
                  setShowProfileMenu(false);
                  onProfileClick();
                }}
                className={`w-full text-left font-medium transition-colors duration-200 flex items-center gap-2 ${isMobile ? 'px-3 py-2 text-xs' : 'px-4 py-3 text-sm'}`}
                style={{ color: APP_COLOURS.BLUE }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${APP_COLOURS.BLUE}20`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <i className={`fas fa-user-cog ${isMobile ? 'text-xs' : ''}`}></i>
                Profile Settings
              </button>

              <button
                onClick={onLogout}
                className={`w-full text-left font-medium transition-colors duration-200 flex items-center gap-2 ${isMobile ? 'px-3 py-2 text-xs' : 'px-4 py-3 text-sm'}`}
                style={{ color: APP_COLOURS.ERROR }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${APP_COLOURS.ERROR}20`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <i className={`fas fa-sign-out-alt ${isMobile ? 'text-xs' : ''}`}></i>
                Logout
              </button>
            </div>
          )}
        </div>
        </div>
      </div>
    </header>
  );
};

export default Header;

